import { INotaDuplicada } from '../../../../hooks/useNotasDuplicadas';

/**
 * Como o ETL juntou as notas (`grupo_duplicata`):
 * - `valor`: mesmo número + CNPJ + valor (`610_09520624000104_250.00`)
 * - `chave`: mesma chave eletrônica + CNPJ (`4_21363253000108_chave:5208…`),
 *   pega a mesma nota importada duas vezes com série, valor ou situação diferentes.
 */
export type MotivoDuplicada = 'valor' | 'chave';

/** Campos que podem mudar entre os lançamentos de um mesmo grupo. */
export type CampoComparado = 'numero' | 'serie' | 'cadastro' | 'data' | 'valor';

export const ROTULO_CAMPO: Record<CampoComparado, string> = {
  numero: 'número',
  serie: 'série',
  cadastro: 'cadastro do cliente/fornecedor',
  data: 'data',
  valor: 'valor',
};

export const ROTULO_MOTIVO: Record<MotivoDuplicada, string> = {
  valor: 'Mesmo número, CNPJ e valor',
  chave: 'Mesma chave eletrônica',
};

export interface IPeriodo {
  inicio?: string | null;
  fim?: string | null;
}

export interface IGrupoDuplicada {
  id: string;
  motivo: MotivoDuplicada;
  chave: string | null;
  notas: INotaDuplicada[];
  diferencas: CampoComparado[];
  /** Lançamentos do grupo fora do período filtrado (o grupo vem inteiro). */
  foraDoPeriodo: number;
  /**
   * Um lançamento a R$ 0 (provável cancelada) e outro com valor: a nota foi
   * cancelada, mas o lançamento com valor continua no Domínio (caso da 1389).
   */
  canceladaEAtiva: boolean;
  tipo: string | null;
  contraparte: string | null;
  cnpj: string | null;
  ultimaData: string | null;
}

const SEP_CHAVE = '_chave:';

export const motivoDoGrupo = (
  grupo: string | null | undefined,
): { motivo: MotivoDuplicada; chave: string | null } => {
  const i = (grupo ?? '').indexOf(SEP_CHAVE);
  return i >= 0
    ? { motivo: 'chave', chave: (grupo as string).slice(i + SEP_CHAVE.length) || null }
    : { motivo: 'valor', chave: null };
};

/** O backend manda Decimal como texto ("6699.00"). */
export const valorDaNota = (n: Pick<INotaDuplicada, 'vcontabil'>): number => {
  const v = Number(n.vcontabil);
  return Number.isFinite(v) ? v : 0;
};

/**
 * A tabela de duplicadas não traz a situação da nota. No Domínio, o lançamento
 * cancelado vem quase sempre com valor R$ 0, então é o sinal que a tela usa.
 */
export const provavelCancelada = (n: Pick<INotaDuplicada, 'vcontabil'>): boolean =>
  valorDaNota(n) === 0;

export const dataDaNota = (n: INotaDuplicada): string | null => n.data_considerar || n.emissao;

export const noPeriodo = (n: INotaDuplicada, periodo?: IPeriodo): boolean => {
  const d = dataDaNota(n);
  if (!periodo?.inicio || !periodo?.fim || !d) return true;
  return d >= periodo.inicio && d <= periodo.fim;
};

// "U" e "u" são a mesma série para o Domínio; só conta como diferença o que muda de fato.
const normaliza = (v: unknown) =>
  String(v ?? '')
    .trim()
    .toUpperCase();

const LEITORES: Record<CampoComparado, (n: INotaDuplicada) => string> = {
  numero: n => normaliza(n.numero_nota),
  serie: n => normaliza(n.serie),
  cadastro: n => normaliza(n.codi_clifor),
  data: n => normaliza(dataDaNota(n)),
  valor: n => valorDaNota(n).toFixed(2),
};

export const camposQueDiferem = (notas: INotaDuplicada[]): CampoComparado[] =>
  (Object.keys(LEITORES) as CampoComparado[]).filter(
    campo => new Set(notas.map(LEITORES[campo])).size > 1,
  );

/** O valor da nota destoa do resto do grupo (para pintar só a célula que muda). */
export const destoa = (n: INotaDuplicada, campo: CampoComparado, grupo: IGrupoDuplicada) => {
  if (!grupo.diferencas.includes(campo)) return false;
  const meu = LEITORES[campo](n);
  const iguais = grupo.notas.filter(o => LEITORES[campo](o) === meu).length;
  // Em um par, as duas células mudam; em grupos maiores, só a minoria.
  return grupo.notas.length <= 2 || iguais <= grupo.notas.length / 2;
};

export const montarGrupos = (notas: INotaDuplicada[], periodo?: IPeriodo): IGrupoDuplicada[] => {
  const porGrupo = new Map<string, INotaDuplicada[]>();
  notas.forEach(n => {
    const lista = porGrupo.get(n.grupo_duplicata) ?? [];
    lista.push(n);
    porGrupo.set(n.grupo_duplicata, lista);
  });
  const grupos: IGrupoDuplicada[] = [];
  porGrupo.forEach((lista, id) => {
    const ordenadas = [...lista].sort(
      (a, b) =>
        (dataDaNota(a) ?? '').localeCompare(dataDaNota(b) ?? '') || a.codi_nota - b.codi_nota,
    );
    const { motivo, chave } = motivoDoGrupo(id);
    const tipos = new Set(ordenadas.map(n => n.tipo_nota));
    const ultima = ordenadas[ordenadas.length - 1];
    grupos.push({
      id,
      motivo,
      chave,
      notas: ordenadas,
      diferencas: camposQueDiferem(ordenadas),
      foraDoPeriodo: ordenadas.filter(n => !noPeriodo(n, periodo)).length,
      canceladaEAtiva:
        ordenadas.some(provavelCancelada) && ordenadas.some(n => !provavelCancelada(n)),
      tipo: tipos.size === 1 ? ordenadas[0].tipo_nota : null,
      contraparte: ordenadas.find(n => n.nome_clifor)?.nome_clifor ?? null,
      cnpj: ordenadas[0].cgce_clifor || null,
      ultimaData: dataDaNota(ultima),
    });
  });
  // Mais recentes primeiro; no empate, os que só a chave pegou (os mais difíceis de ver).
  return grupos.sort(
    (a, b) =>
      (b.ultimaData ?? '').localeCompare(a.ultimaData ?? '') ||
      Number(b.motivo === 'chave') - Number(a.motivo === 'chave') ||
      a.id.localeCompare(b.id),
  );
};

export const resumoDosGrupos = (grupos: IGrupoDuplicada[]) => ({
  grupos: grupos.length,
  lancamentos: grupos.reduce((s, g) => s + g.notas.length, 0),
  porChave: grupos.filter(g => g.motivo === 'chave').length,
  canceladaEAtiva: grupos.filter(g => g.canceladaEAtiva).length,
  comForaDoPeriodo: grupos.filter(g => g.foraDoPeriodo > 0).length,
});
